import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { TwoFactorService } from '../../services/two-factor.service';
import { TwoFactorStatusResponse } from '../../models/two-factor.models';

const SESSION_DISMISS_KEY = 'planaudit.twoFactorReminderDismissed';

/**
 * Globaler Status-Tracker für die 2FA-Pflicht. Lädt nach jedem Login
 * den 2FA-Status, hält ein Signal mit der Frist und steuert das
 * One-Shot-Modal sowie den dauerhaften Banner.
 *
 * Modal-Auto-Open: einmal pro Browser-Session (sessionStorage-Flag) —
 * nach Klick auf "Später" oder "Jetzt einrichten" wird das Flag gesetzt
 * und der Reminder taucht erst beim nächsten Browser-Tab/Restart wieder
 * auf. Banner bleibt unabhängig vom Flag dauerhaft sichtbar.
 */
@Injectable({ providedIn: 'root' })
export class TwoFactorEnforcementService {
  private readonly auth = inject(AuthService);
  private readonly twoFactor = inject(TwoFactorService);

  readonly status = signal<TwoFactorStatusResponse | null>(null);
  readonly modalOpen = signal(false);

  constructor() {
    effect(() => {
      const user = this.auth.currentUser();
      if (!user) {
        this.status.set(null);
        this.modalOpen.set(false);
        return;
      }
      this.refresh();
    });

    // Auto-Open des Modals nach Login: sobald mustEnable() das erste Mal
    // true wird und der User es nicht schon in dieser Session weggeklickt
    // hat, popt das Modal auf.
    effect(() => {
      if (!this.mustEnable()) return;
      if (this.modalOpen()) return;
      if (this.dismissedThisSession()) return;
      this.modalOpen.set(true);
    });
  }

  refresh(): void {
    this.twoFactor.status().subscribe({
      next: (s) => this.status.set(s),
      error: () => this.status.set(null),
    });
  }

  /**
   * User ist 2FA-pflichtig und hat es noch nicht aktiviert. SSO-Sessions mit
   * IdP-MFA-Vertrauen erfüllen die Pflicht bereits → kein Banner/Modal.
   */
  readonly mustEnable = computed(() => {
    const s = this.status();
    return s !== null && s.required && !s.enabled && !s.mfaSatisfiedViaSso;
  });

  /** Tage bis zur Frist (gerundet, mind. 0). null wenn keine Frist gesetzt. */
  readonly daysRemaining = computed<number | null>(() => {
    const s = this.status();
    if (!s?.graceDeadline) return null;
    const ms = new Date(s.graceDeadline).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / 86_400_000));
  });

  /** "27.04.2026" oder null. */
  readonly graceDeadlineLabel = computed<string | null>(() => {
    const s = this.status();
    if (!s?.graceDeadline) return null;
    try {
      return new Date(s.graceDeadline).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  });

  /** Pflicht aktiv und nicht (mehr) in der Grace-Period. */
  readonly isOverdue = computed(() => {
    const s = this.status();
    if (!s?.required || s.enabled || s.mfaSatisfiedViaSso) return false;
    return !s.inGracePeriod;
  });

  dismissModalForSession(): void {
    this.markDismissedThisSession();
    this.modalOpen.set(false);
  }

  /**
   * Schliesst das Modal nach einer aktiven Aktion (z.B. Klick auf
   * "Jetzt einrichten"). Setzt das Session-Flag mit, sonst würde der
   * Auto-Open-Effect das Modal sofort wieder aufmachen, weil
   * mustEnable() bis zur tatsächlichen Aktivierung true bleibt.
   */
  closeModalAfterAction(): void {
    this.markDismissedThisSession();
    this.modalOpen.set(false);
  }

  private markDismissedThisSession(): void {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      // ignore — sessionStorage kann in Privacy-Modi blockiert sein
    }
  }

  private dismissedThisSession(): boolean {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }
}
